/**
 * Model Selection Component
 * 
 * Custom dropdown for selecting a model from the canonical model catalog.
 * Catalog-driven (ISS-447): lists catalog entries (provider-agnostic); the
 * serving provider is resolved by policy at runtime (ADR-041/ADR-042).
 * Includes a search box to filter the catalog list by display name, slug
 * or capability keywords.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Check, Bot, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AvailableModel, ModelCategory } from '../types';

// =============================================================================
// Types
// =============================================================================

interface ModelSelectProps {
  value: string | undefined;
  onChange: (modelId: string | undefined) => void;
  models: AvailableModel[];
  category: ModelCategory;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

/**
 * Format context window size (e.g., 200000 -> "200K")
 */
function formatContextWindow(size: number): string {
  return `${(size / 1000).toFixed(0)}K`;
}

/**
 * Build the searchable haystack for a catalog model: display name, slug and
 * capability keywords ("vision", "tools", "context").
 */
function modelSearchHaystack(model: AvailableModel): string {
  const tokens = [
    model.display_name,
    model.model_id,
    model.catalog_slug ?? '',
    model.supports_vision ? 'vision' : '',
    model.supports_tools ? 'tools' : '',
  ];
  return tokens.filter(Boolean).join(' ').toLowerCase();
}

// =============================================================================
// Component
// =============================================================================

export function ModelSelect({
  value,
  onChange,
  models,
  category: _category,
  required = false,
  disabled = false,
  placeholder = 'Select a model...',
}: ModelSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [isOpen]);

  const selectedModel = models.find((m) => m.id === value);

  const filteredModels = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return models;
    return models.filter((m) => modelSearchHaystack(m).includes(term));
  }, [models, searchTerm]);

  const handleToggle = () => {
    if (disabled) return;
    const nextOpen = !isOpen;
    setIsOpen(nextOpen);
    if (!nextOpen) setSearchTerm('');
  };

  const handleSelect = (model: AvailableModel) => {
    onChange(model.id);
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange(undefined);
    setSearchTerm('');
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={rootRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={cn(
          'w-full px-3 py-2 text-left bg-white dark:bg-gray-800',
          'border border-gray-300 dark:border-gray-600 rounded-lg',
          'flex items-center justify-between',
          'focus:outline-none focus:ring-2 focus:ring-blue-500',
          'transition-colors',
          disabled
            ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed opacity-60'
            : 'hover:border-gray-400 dark:hover:border-gray-500',
          isOpen && 'ring-2 ring-blue-500'
        )}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {selectedModel ? (
          <div className="flex items-center gap-2 overflow-hidden">
            <Bot className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <span className="truncate">{selectedModel.display_name}</span>
            <span className="text-xs text-gray-500">({selectedModel.model_id})</span>
          </div>
        ) : (
          <span className="text-gray-400">{placeholder}</span>
        )}
        <ChevronDown
          className={cn(
            'w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ml-2',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Options List — catalog entries, provider-agnostic */}
          <div 
            className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto flex flex-col"
            role="listbox"
          >
            {/* Search box — filters the catalog list */}
            <div className="sticky top-0 z-10 px-2 pt-2 pb-1.5 bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  autoFocus
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search models..."
                  aria-label="Search models"
                  className={cn(
                    'w-full pl-8 pr-2 py-1.5 text-sm rounded-md',
                    'bg-gray-50 dark:bg-gray-700',
                    'border border-gray-200 dark:border-gray-600',
                    'text-gray-900 dark:text-gray-100',
                    'placeholder:text-gray-400 dark:placeholder:text-gray-500',
                    'focus:outline-none focus:ring-2 focus:ring-blue-500'
                  )}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>

            {/* Clear option for non-required fields */}
            {!required && (
              <button
                type="button"
                onClick={handleClear}
                className="w-full px-3 py-2 text-left text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700"
                role="option"
                aria-selected={!value}
              >
                None (inherit default)
              </button>
            )}

            {/* Catalog models (filtered by search) */}
            {filteredModels.map((model) => (
              <button
                key={model.id}
                type="button"
                onClick={() => handleSelect(model)}
                className={cn(
                  'w-full px-3 py-2 text-left flex items-center justify-between',
                  'hover:bg-gray-100 dark:hover:bg-gray-700',
                  value === model.id && 'bg-blue-50 dark:bg-blue-900/30'
                )}
                role="option"
                aria-selected={value === model.id}
              >
                <div className="flex items-center gap-2 overflow-hidden">
                  <Bot className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <div className="overflow-hidden min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate font-medium text-gray-900 dark:text-gray-100">
                        {model.display_name}
                      </span>
                      {model.is_default && (
                        <span className="text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 px-1.5 py-0.5 rounded flex-shrink-0">
                          default
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {model.model_id}
                      {model.context_window > 0 && ` • ${formatContextWindow(model.context_window)} context`}
                      {model.supports_vision && ' • Vision'}
                      {model.supports_tools && ' • Tools'}
                    </div>
                  </div>
                </div>
                {value === model.id && (
                  <Check className="w-4 h-4 text-blue-500 flex-shrink-0 ml-2" />
                )}
              </button>
            ))}

            {/* Empty State */}
            {models.length === 0 && (
              <div className="px-3 py-4 text-center text-gray-500 dark:text-gray-400">
                No models available for this category
              </div>
            )}
            {models.length > 0 && filteredModels.length === 0 && (
              <div className="px-3 py-4 text-center text-gray-500 dark:text-gray-400">
                No models match your search
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
